// src/utils/music.js
export default class MusicManager {
    constructor() {
        this.backgroundAudio = null
        this.soundEffects = {}
        this.currentBgMusicKey = null
        this.isBgMusicPlaying = false
        this.bgMusicVolume = 0.5 // 默认背景音乐音量
        this.soundEffectsVolume = 0.7 // 默认音效音量
    }

    /**
     * 预加载音频资源
     * @param {Object} resources - 音频资源配置 { key: { type: 'bg|effect', url: 'path/to/audio.mp3' } }
     */
    init(resources) {
        Object.entries(resources).forEach(([key, config]) => {
            if (config.type === 'bg') {
                // 背景音乐只需要创建一个Audio对象
                this.soundEffects[key] = new Audio(config.url)
                this.soundEffects[key].loop = true
                this.soundEffects[key].volume = this.bgMusicVolume
            } else {
                // 音效可以创建多个实例，避免重叠播放被中断
                this.soundEffects[key] = () => {
                    const audio = new Audio(config.url)
                    audio.volume = this.soundEffectsVolume
                    audio.play()
                    return audio
                }
            }
        })
    }

    /**
     * 播放背景音乐
     * @param {string} key - 音乐key
     * @param {number} [volume] - 可选音量设置(0-1)
     */
    playBackgroundMusic(key, volume) {
        if (this.currentBgMusicKey === key && this.isBgMusicPlaying) return

        // 停止当前播放的背景音乐
        this.stopBackgroundMusic()

        const audio = this.soundEffects[key]
        if (audio) {
            this.backgroundAudio = audio
            this.currentBgMusicKey = key
            this.isBgMusicPlaying = true

            if (volume !== undefined) {
                this.setBgMusicVolume(volume)
            }

            audio.currentTime = 0
            audio.play().catch(e => console.error('背景音乐播放失败:', e))
        }
    }

    /**
     * 停止背景音乐
     */
    stopBackgroundMusic() {
        if (this.backgroundAudio) {
            this.backgroundAudio.pause()
            this.backgroundAudio.currentTime = 0
        }
        this.isBgMusicPlaying = false
        this.currentBgMusicKey = null
    }

    /**
     * 暂停背景音乐
     */
    pauseBackgroundMusic() {
        if (this.backgroundAudio && this.isBgMusicPlaying) {
            this.backgroundAudio.pause()
            this.isBgMusicPlaying = false
        }
    }

    /**
     * 恢复背景音乐
     */
    resumeBackgroundMusic() {
        if (this.backgroundAudio && !this.isBgMusicPlaying) {
            this.backgroundAudio.play().catch(e => console.error('背景音乐恢复失败:', e))
            this.isBgMusicPlaying = true
        }
    }

    /**
     * 设置背景音乐音量
     * @param {number} volume - 音量(0-1)
     */
    setBgMusicVolume(volume) {
        this.bgMusicVolume = Math.max(0, Math.min(1, volume))
        if (this.backgroundAudio) {
            this.backgroundAudio.volume = this.bgMusicVolume
        }
    }

    /**
     * 设置音效音量
     * @param {number} volume - 音量(0-1)
     */
    setSoundEffectsVolume(volume) {
        this.soundEffectsVolume = Math.max(0, Math.min(1, volume))
    }

    /**
     * 播放一次性音效
     * @param {string} key - 音效key
     * @param {number} [volume] - 可选音量设置(0-1)
     * @return {Audio} 返回Audio对象以便进一步控制
     */
    playSoundEffect(key, volume) {
        const effect = this.soundEffects[key]
        if (typeof effect === 'function') {
            const audio = effect()
            if (volume !== undefined) {
                audio.volume = volume
            }
            return audio
        }
        return null
    }

    /**
     * 停止所有音效
     */
    stopAllSoundEffects() {
        Object.values(this.soundEffects).forEach(effect => {
            if (effect instanceof Audio) {
                effect.pause()
                effect.currentTime = 0
            }
        })
    }
}

// 创建单例导出
export const music = new MusicManager()